/**
 * WorkflowStepEditor.jsx
 *
 * Auto-generated form from ActionDef.parameters.
 * Renders controlled MUI fields based on resolved field types.
 */

import { useCallback } from 'react';
import {
    Box,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Typography,
    FormHelperText,
} from '@mui/material';
import { resolveFieldType, FieldType } from './constants';
import { paramRangeError } from './stepParamOverrides';

/**
 * @param {Object} props
 * @param {import('../../../gibbs/registry/schema').ActionDef} props.actionDef
 * @param {Object} props.params - Current parameter values for this step
 * @param {Function} props.onParamChange - (stepIndex, paramName, value) => void
 * @param {number} props.stepIndex
 */
export default function WorkflowStepEditor({
    actionDef, params, onParamChange, stepIndex, disabled = false,
}) {
    const parameters = actionDef?.parameters || [];

    const handleChange = useCallback((paramName) => (evt) => {
        const { type, checked, value } = evt.target;
        onParamChange(stepIndex, paramName, type === 'checkbox' ? checked : value);
    }, [stepIndex, onParamChange]);

    if (parameters.length === 0) {
        return (
            <Box sx={{ py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                    No configurable parameters.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex', flexDirection: 'column', gap: 1.5, py: 1,
        }}
        >
            {parameters.map((paramDef) => {
                const fieldType = resolveFieldType(paramDef);
                const value = params?.[paramDef.name] ?? paramDef.defaultValue ?? '';
                const optionLabels = paramDef.optionLabels || {};
                const label = paramDef.name
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (s) => s.toUpperCase());

                switch (fieldType) {
                    case FieldType.SELECT:
                        return (
                            <FormControl key={paramDef.name} size="small" fullWidth>
                                <InputLabel>{label}</InputLabel>
                                <Select
                                    disabled={disabled}
                                    value={value}
                                    label={label}
                                    onChange={handleChange(paramDef.name)}
                                >
                                    {(paramDef.options || []).map((opt) => (
                                        <MenuItem key={opt} value={opt}>
                                            {optionLabels[opt] || opt}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {paramDef.description && (
                                    <FormHelperText>{paramDef.description}</FormHelperText>
                                )}
                            </FormControl>
                        );

                    case FieldType.NUMBER:
                    case FieldType.INCREMENT: {
                        // min/max only cap the spinner, so a typed out-of-range
                        // value needs its own complaint on the field itself.
                        const rangeError = paramRangeError(paramDef, value);
                        return (
                            <TextField
                                key={paramDef.name}
                                label={label}
                                type="number"
                                size="small"
                                fullWidth
                                value={value}
                                disabled={disabled}
                                onChange={handleChange(paramDef.name)}
                                error={Boolean(rangeError)}
                                helperText={rangeError || paramDef.description}
                                inputProps={{
                                    min: paramDef.constraints?.min,
                                    max: paramDef.constraints?.max,
                                }}
                            />
                        );
                    }

                    case FieldType.CHECKBOX:
                        return (
                            <Box key={paramDef.name}>
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            disabled={disabled}
                                            checked={!!value}
                                            onChange={handleChange(paramDef.name)}
                                            size="small"
                                        />
                                      )}
                                    label={label}
                                />
                                {paramDef.description && (
                                    <FormHelperText sx={{ mt: -0.5, ml: 4 }}>
                                        {paramDef.description}
                                    </FormHelperText>
                                )}
                            </Box>
                        );

                    case FieldType.READONLY:
                        return (
                            <Box key={paramDef.name}>
                                <Typography variant="caption" color="text.secondary">
                                    {label}
                                </Typography>
                                <Typography variant="body2">
                                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                </Typography>
                                {paramDef.description && (
                                    <FormHelperText>{paramDef.description}</FormHelperText>
                                )}
                            </Box>
                        );

                    case FieldType.TEXT:
                    default:
                        return (
                            <TextField
                                key={paramDef.name}
                                label={label}
                                size="small"
                                fullWidth
                                value={value}
                                disabled={disabled}
                                onChange={handleChange(paramDef.name)}
                                helperText={paramDef.description}
                            />
                        );
                }
            })}
        </Box>
    );
}
